import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { submitFeedback } from '../services/api'
import { useFormDraft } from '../hooks/useFormDraft'
import useModal from '../hooks/useModal'

// Report Issue modal — opened by the sidebar's "Report Issue" item which
// dispatches a `'open-report-issue'` window event (decoupled trigger so the
// modal can live in Layout while the launcher lives next to Sign Out without
// prop drilling).  Pre-fills the current page URL + user-agent so admins
// triaging the inbox have context without asking back.
export default function ReportIssueButton() {
  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState('')   // '', 'sent', or error message
  const location = useLocation()

  // Auto-save the description as a draft — a browser crash or accidental
  // close mid-report shouldn't wipe a long bug-repro.  Wrap the string in
  // an object so the hook has a stable shape.
  const setForm = (v) => {
    if (typeof v === 'function') {
      setDescription((prev) => v({ description: prev }).description ?? '')
    } else if (v && typeof v === 'object') {
      setDescription(v.description ?? '')
    }
  }
  const draft = useFormDraft(
    'lextrack_draft_report_issue',
    { description },
    setForm,
    { enabled: open, initialForm: { description: '' }, debounceMs: 2000 }
  )

  // Modal keyboard behavior — Esc "minimizes" (preserves the draft), Tab
  // loops within, focus lock.  Consistent with the header Minimize button
  // per feedback #475 — Esc / backdrop-click / Minimize all keep the draft;
  // only explicit Close (×) or Cancel wipes it.
  const { containerProps } = useModal({
    onClose: () => setOpen(false),
    enabled: open,
    autoFocus: false, // We already have a bespoke focus-loop for the textarea
  })

  // Feedback #527 fix — the event handler is registered once (empty deps)
  // and closes over the INITIAL description (''), so the `!description`
  // check always evaluated true — every re-open with a popup chip wiped
  // whatever the user had typed.  Ref lets the handler read the current
  // description at fire time.
  const descriptionRef = useRef(description)
  useEffect(() => { descriptionRef.current = description }, [description])
  useEffect(() => {
    // Feedback #511: chip in DraggablePopup / PopupLayout dispatches this
    // with detail: {source: 'popup', popupTitle: '...'} — pre-fill the
    // description with a bracketed context tag so admins see WHICH popup
    // the user was in when they hit report.
    const handler = (evt) => {
      const detail = evt?.detail
      if (detail?.source === 'popup' && detail.popupTitle && !descriptionRef.current) {
        setDescription(`[Popup: ${detail.popupTitle}]\n\n`)
      }
      setOpen(true)
    }
    window.addEventListener('open-report-issue', handler)
    return () => window.removeEventListener('open-report-issue', handler)
  }, [])

  // When this modal opens, minimize the timer popup so it doesn't cover the
  // form.  Same pattern DraggablePopup uses on mount — this custom modal
  // isn't a DraggablePopup so it had to opt in explicitly. (Feedback #46.)
  useEffect(() => {
    if (open) {
      try { window.dispatchEvent(new Event('minimize-time-popup')) } catch {}
    }
  }, [open])

  // Explicit focus on open + win the cross-window focus race.
  // `autoFocus` alone doesn't work reliably when another OS window
  // (e.g. a pop-out timer) has the actual keyboard focus.  Reports from
  // Shawn (id 70) AND Hestia (2026-06-27): "the box pops up but I can't
  // type in it". We use a longer + denser retry chain + window.focus() to
  // actively pull focus into this window first.
  const textareaRef = useRef(null)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    // Pull OS focus to the current window before trying to focus the field
    // (no-op in browser; in Electron this fights pop-out windows for focus).
    try { window.focus() } catch {}
    // Try to focus the textarea after the modal mounts.  6 retries over
    // ~600ms because cross-window focus stealing in Electron can race
    // and a single 50ms delay isn't always enough.
    const tryFocus = (n) => {
      if (cancelled || n <= 0) return
      const el = textareaRef.current
      if (el) {
        el.focus()
        // Place the caret at the end (or wherever) — visible cursor confirms focus
        try { el.setSelectionRange(el.value.length, el.value.length) } catch {}
      }
      setTimeout(() => tryFocus(n - 1), 100)
    }
    // Two-stage: requestAnimationFrame ensures the DOM is painted before
    // first focus attempt; then the retry chain handles any race.
    requestAnimationFrame(() => { if (!cancelled) tryFocus(6) })
    return () => { cancelled = true }
  }, [open])

  // Full reset — closes the modal AND wipes the description.  Only used on
  // Close (×), Cancel, and after a successful submit.  Minimize (−) should
  // NEVER call this — see minimize() below.
  const reset = () => { setOpen(false); setDescription(''); setStatus('') }
  // Feedback #475 (Shawn 2026-08-04): Minimize preserves the draft.  Only
  // closes the modal; `description` is component-level state and survives
  // the unmount of the modal DOM (ReportIssueButton itself stays mounted in
  // Layout), so on next open the textarea shows what was typed.  Plus
  // useFormDraft persists to localStorage every 2 s — a browser crash
  // between minimize and next open still restores from the draft banner.
  const minimize = () => { setOpen(false); setStatus('') }

  const submit = async (e) => {
    e?.preventDefault?.()
    if (!description.trim()) { setStatus('Please describe the issue.'); return }
    setSubmitting(true); setStatus('')
    try {
      await submitFeedback({
        description: description.trim(),
        url: location.pathname + location.search
      })
      setStatus('sent')
      setDescription('')
      draft.clear()   // wipe the persisted draft — submitted successfully
      setTimeout(reset, 1500)
    } catch (err) {
      setStatus(err.response?.data?.error || 'Could not submit — try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {open && (
        // z-[70] sits above DraggablePopup's zIndex:60 so the modal is on top
        // even in the race where the timer popup is mid-minimize when Report
        // Issue opens.  The minimize event above is the primary fix; this
        // is belt-and-suspenders.
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={minimize}>
          <div {...containerProps} aria-labelledby="report-issue-title"
               className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">💬 Report an Issue</h3>
              <div className="flex items-center gap-2">
                {/* Feedback #475: Minimize preserves the draft; only Close (×) wipes. */}
                <button onClick={minimize} title="Minimize (keeps your draft)" className="text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100">−</button>
                <button onClick={reset} title="Close and discard draft" className="text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100">×</button>
              </div>
            </div>

            {status === 'sent' ? (
              <div className="p-8 text-center">
                <div className="text-4xl mb-2">✅</div>
                <p className="text-green-700 font-medium">Thank you — sent to your admin.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 space-y-4">
                {draft.banner}
                <div>
                  <label className="text-xs uppercase font-medium text-gray-500">What happened?</label>
                  <textarea
                    ref={textareaRef}
                    autoFocus
                    tabIndex={0}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    // Both mousedown + click force focus so even if a cross-window
                    // race steals it between events, ONE of these wins.
                    onMouseDown={(e) => { try { window.focus() } catch {}; e.currentTarget.focus() }}
                    onClick={(e) => e.currentTarget.focus()}
                    rows={6}
                    placeholder="Describe the bug, confusing UI, or anything else worth flagging. The current page URL and your browser info are sent automatically."
                    className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={5000}
                  />
                  <p className="text-xs text-gray-400 mt-1">{description.length} / 5000</p>
                </div>

                <div className="text-xs text-gray-500">
                  Context attached automatically: <code className="bg-gray-100 px-1 rounded">{location.pathname}{location.search}</code>
                </div>

                {status && status !== 'sent' && (
                  <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{status}</div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={reset} disabled={submitting}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={submitting || !description.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
                    {submitting ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  )
}
